import React, { useState, memo, useCallback } from "react";
import PropTypes from "prop-types";
import { useUpdateAutoText } from "./hooks/useUpdateAutoText";
import { toast } from "react-toastify";
import { Row, Col, Button, Form } from "react-bootstrap";
import LoadingInput from "components/common/LoadingInput/LoadingInput";

const styles = {
  btnWrapper: {
    flex: "0 0 10%",
  },
};

/**
 * EditableTextItem component for displaying and editing text items
 * @param {Object} props
 * @param {Object} props.detail - The text item details
 * @param {boolean} props.lg - Whether to use large layout
 */
const EditableTextItem = React.memo(({ detail, lg = false }) => {
  const [editState, setEditState] = useState({
    isEditing: false,
    currentText: detail.displayText,
    originalText: detail.displayText,
  });

  const { mutate: updateAutoTextMutation, isPending: isUpdating } =
    useUpdateAutoText();

  const handleSuccess = useCallback(() => {
    toast.success("Successfully updated the statement.");
    setEditState((prev) => ({
      ...prev,
      isEditing: false,
      originalText: prev.currentText,
    }));
  }, []);

  const handleError = useCallback((errMsg) => {
    toast.error(errMsg || "Failed to update statement");
  }, []);

  const handleUpdate = useCallback(() => {
    if (!editState.currentText.trim()) {
      toast.error("Text cannot be empty");
      return;
    }

    updateAutoTextMutation(
      {
        id: detail._id,
        data: { displayText: editState.currentText },
      },
      {
        onSuccess: handleSuccess,
        onError: (error) => handleError(error.message),
      }
    );
  }, [
    detail._id,
    editState.currentText,
    updateAutoTextMutation,
    handleSuccess,
    handleError,
  ]);

  const handleCancel = useCallback(() => {
    setEditState((prev) => ({
      ...prev,
      isEditing: false,
      currentText: prev.originalText,
    }));
  }, []);

  const handleEdit = useCallback(() => {
    setEditState((prev) => ({
      ...prev,
      isEditing: true,
    }));
  }, []);

  const handleTextChange = useCallback((e) => {
    setEditState((prev) => ({
      ...prev,
      currentText: e.target.value,
    }));
  }, []);

  return (
    <Row>
      <Col lg={lg ? null : 5}>
        <Form.Group>
          <LoadingInput
            onChange={handleTextChange}
            value={editState.currentText}
            disabled={!editState.isEditing || isUpdating}
            isLoading={isUpdating}
            type="text"
            style={{ marginBottom: "15px" }}
          />
        </Form.Group>
      </Col>

      <Col style={styles.btnWrapper}>
        {editState.isEditing ? (
          <Button
            className="__btn_expanded"
            disabled={isUpdating}
            variant="outline-success"
            onClick={handleUpdate}
          >
            {isUpdating ? "Saving..." : "Save"}
          </Button>
        ) : (
          <Button
            className="__btn_expanded"
            variant="outline-secondary"
            disabled={isUpdating}
            onClick={handleEdit}
          >
            Edit
          </Button>
        )}
      </Col>
      <Col style={styles.btnWrapper}>
        {editState.isEditing && (
          <Button
            className="__btn_expanded"
            variant="danger"
            disabled={isUpdating}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </Col>
    </Row>
  );
});

/**
 * SectionTitle component for displaying section headers
 */
const SectionTitle = memo(({ title }) => (
  <Row style={{ marginTop: "1em" }}>
    <Col>
      <Form.Label>{title}</Form.Label>
    </Col>
    <Col style={styles.btnWrapper} />
  </Row>
));

SectionTitle.propTypes = {
  title: PropTypes.string.isRequired,
};

/**
 * DataItem component for rendering a section of editable text items
 */
const DataItem = memo(({ info, nestedData, lg, header }) => (
  <>
    <SectionTitle title={header} />
    {nestedData?.map((detail) => (
      <EditableTextItem key={detail._id} detail={detail} lg={lg} />
    ))}
  </>
));

DataItem.propTypes = {
  info: PropTypes.object.isRequired,
  nestedData: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      displayText: PropTypes.string.isRequired,
    })
  ),
  lg: PropTypes.bool,
  header: PropTypes.string.isRequired,
};

/**
 * DisplayData component for rendering the main data display
 */
const DisplayData = memo(({ autoTextsData, lg = false }) => {
  if (!autoTextsData?.length) {
    return null;
  }

  return (
    <>
      {autoTextsData.map((info) => (
        <DataItem
          lg={lg}
          key={info._id}
          info={info}
          header={info._id}
          nestedData={info?.data}
        />
      ))}
    </>
  );
});

DisplayData.propTypes = {
  autoTextsData: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string.isRequired,
          displayText: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  lg: PropTypes.bool,
};

EditableTextItem.propTypes = {
  detail: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    displayText: PropTypes.string.isRequired,
  }).isRequired,
  lg: PropTypes.bool,
};
export default DisplayData;
